// ============================================
// CODEMINE WORKSPACE PAGE - PROFESSIONAL RESPONSIVE DESIGN
// Desktop: 30% Chat | 70% Preview with Files/Code toggle
// Mobile: Chat/Preview toggle
// ============================================

import React, { useState, useEffect, useRef } from 'react';
import { AnimatedCodePreview } from '../components/AnimatedCodePreview';
import { TokenUsageDisplay } from '../components/TokenUsageDisplay';
import { useWorkerAPI } from '../lib/worker-api';
import { useAuth } from '../components/context/AuthContext';
import { CodemineLogo, CodemineLogoSimple } from '../components/CodemineLogo';
import { CodeyThinking, PlanningMessage, ProgressMessage } from '../components/CodeyThinking';

const WORKER_URL = 'https://codemine-api-worker.shazim-rv11-675.workers.dev';

interface GeneratedFile {
  path: string;
  content: string;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'codey';
  content: string;
  type?: 'plan' | 'progress' | 'file' | 'validation' | 'complete';
  data?: any;
  timestamp: number;
}

interface Plan {
  projectName: string;
  enhancedPrompt: string;
  features: string[];
  design: { style: string; colorScheme: string; layout: string };
  techStack: string[];
  projectType: string;
  files: string[];
}

interface Project {
  name: string;
  description: string;
  type: string;
  framework: string;
}

interface WorkspacePageProps {
  theme?: string;
  initialPrompt?: string | null;
  clearInitialPrompt?: () => void;
  projectId?: number | null;
}

export default function WorkspacePage({ theme = 'dark', initialPrompt, clearInitialPrompt, projectId }: WorkspacePageProps) {
  const workerAPI = useWorkerAPI();
  const { user } = useAuth();

  // State
  const [prompt, setPrompt] = useState('');
  const [jobId, setJobId] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('idle');
  const [files, setFiles] = useState<Record<string, string>>({});
  const [project, setProject] = useState<Project | null>(null);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [modifiedPlan, setModifiedPlan] = useState<Plan | null>(null);
  const [progress, setProgress] = useState(0);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome-' + Date.now(),
      role: 'codey',
      content: "Hey there! 👋 I'm Codey, your friendly AI coding companion.\n\nI'm here to help bring your ideas to life! Whether it's a sleek website, a mobile app, or even a game - just tell me what you're thinking and I'll build it for you.\n\nSo... what cool thing should we create together today? 🚀",
      timestamp: Date.now()
    }
  ]);
  const [canContinue, setCanContinue] = useState(false);
  const [validationReport, setValidationReport] = useState<any>(null);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const [fileSearchQuery, setFileSearchQuery] = useState('');
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'connecting' | 'disconnected'>('disconnected');
  const [showQuicError, setShowQuicError] = useState(false);
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [modifyInput, setModifyInput] = useState('');
  const [displayedMessages, setDisplayedMessages] = useState<ChatMessage[]>([]);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [thinkingTime, setThinkingTime] = useState(0);

  // Use theme from App.tsx instead of system detection
  const isDarkMode = theme === 'dark';

  // View state
  const [desktopView, setDesktopView] = useState<'preview' | 'files'>('preview'); // Desktop: toggle between preview and files
  const [mobileView, setMobileView] = useState<'chat' | 'preview'>('chat'); // Mobile: toggle between chat and preview

  const eventSourceRef = useRef<EventSource | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Animate messages one at a time (for Codey messages only)
  useEffect(() => {
    if (chatMessages.length === 0) {
      setDisplayedMessages([]);
      setCurrentMessageIndex(0);
      return;
    }

    // Show user messages immediately, animate only Codey messages during generation
    if (status === 'generating' || status === 'awaiting-approval') {
      // Find the last user message index
      let lastUserIndex = -1;
      for (let i = chatMessages.length - 1; i >= 0; i--) {
        if (chatMessages[i].role === 'user') {
          lastUserIndex = i;
          break;
        }
      }

      // Show all messages up to and including last user message
      const baseMessages = lastUserIndex >= 0 ? chatMessages.slice(0, lastUserIndex + 1) : [];

      // Get Codey messages after the last user message
      const codeyMessagesAfterUser = lastUserIndex >= 0 ? chatMessages.slice(lastUserIndex + 1) : chatMessages;
      const codeyCount = codeyMessagesAfterUser.filter(m => m.role === 'codey' && (m.type === 'progress' || m.type === 'file')).length;

      if (codeyCount === 0) {
        setDisplayedMessages(chatMessages);
        return;
      }

      // Show only the latest Codey progress/file message (one at a time)
      const latestCodeyMessage = codeyMessagesAfterUser[codeyMessagesAfterUser.length - 1];
      if (latestCodeyMessage && latestCodeyMessage.role === 'codey' && (latestCodeyMessage.type === 'progress' || latestCodeyMessage.type === 'file')) {
        setDisplayedMessages([...baseMessages, latestCodeyMessage]);
      } else {
        setDisplayedMessages(chatMessages);
      }
    } else {
      // When not generating, show all messages
      setDisplayedMessages(chatMessages);
    }
  }, [chatMessages, status]);

  // Cleanup EventSource on unmount
  useEffect(() => {
    return () => {
      eventSourceRef.current?.close();
    };
  }, []);

  // Detect QUIC protocol errors
  useEffect(() => {
    const handleError = (e: ErrorEvent) => {
      if (e.message?.includes('QUIC') || e.message?.includes('ERR_QUIC_PROTOCOL_ERROR')) {
        setShowQuicError(true);
        console.warn('⚠️ QUIC protocol error detected. Please disable QUIC in chrome://flags/#enable-quic');
      }
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  // Select initial file when files update
  useEffect(() => {
    const keys = Object.keys(files || {});
    if (keys.length === 0) {
      setSelectedFile(null);
      return;
    }
    const preferred = ['index.html', 'src/App.tsx', 'src/App.jsx', 'App.tsx', 'App.jsx'];
    const first = preferred.find(p => keys.includes(p)) || keys[0];
    if (!selectedFile || !files[selectedFile]) {
      setSelectedFile(first);
    }
  }, [files]);

  // Load project for editing if projectId is provided
  useEffect(() => {
    if (projectId && user) {
      loadProjectForEditing(projectId);
    }
  }, [projectId, user]);

  const loadProjectForEditing = async (id: number) => {
    try {
      addCodeyMessage('📂 Loading your project...', 'progress');

      // Fetch project details from API
      const response = await workerAPI.getProject(id.toString());

      if (response.success && response.data?.project) {
        const proj = response.data.project;

        // Set project details
        setProject({
          name: proj.name,
          description: proj.description || 'Loaded project',
          type: proj.project_type || 'website',
          framework: proj.framework || 'React'
        });

        // Load files from R2 or use getProjectFiles endpoint
        try {
          const filesResponse = await workerAPI.getProjectFiles(proj.id);
          if (filesResponse.success && filesResponse.data?.files) {
            setFiles(filesResponse.data.files);
            addCodeyMessage(`✅ Loaded "${proj.name}" with ${Object.keys(filesResponse.data.files).length} files!`, 'progress');
          } else {
            addCodeyMessage(`⚠️ Loaded "${proj.name}" but files are not available. You can generate new content!`, 'progress');
          }
        } catch (fileErr) {
          addCodeyMessage(`⚠️ Loaded "${proj.name}" but couldn't load files. You can generate new content!`, 'progress');
        }

        setStatus('complete');
        setSavedProjectId(parseInt(proj.id));
      } else {
        throw new Error('Failed to load project');
      }
    } catch (err) {
      console.error('Load project error:', err);
      addCodeyMessage('❌ Failed to load project. Starting fresh workspace.');
    }
  };

  // Plan modal hotkeys
  useEffect(() => {
    if (!showPlanModal) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setShowPlanModal(false);
      if (e.key === 'Enter' && e.metaKey) {
        approvePlan();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showPlanModal, jobId]);

  // ============================================
  // MAIN: GENERATE PROJECT
  // ============================================
  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt describing what you want to build');
      return;
    }

    // Reset state
    setStatus('generating');
    setError('');
    setFiles({});
    setPlan(null);
    setProject(null);
    setProgress(0);
    setCanContinue(false);
    setValidationReport(null);
    setCurrentStep('Initializing...');

    // Add user message to chat
    addUserMessage(prompt.trim());

    try {
      // First, check intent with chat endpoint
      const chatResponse = await fetch(`${WORKER_URL}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: prompt.trim(),
          conversationHistory: chatMessages.slice(-6),
          projectFiles: files // Pass existing files for modification detection
        })
      });

      const chatData = await chatResponse.json();

      if (chatData && typeof chatData === 'object') {
        if ('type' in chatData && (chatData as any).type === 'modification') {
          // File modification response
          if ('message' in chatData) {
            addCodeyMessage((chatData as any).message);
          }
          if ('modifiedFiles' in chatData || 'allFiles' in chatData) {
            const newFiles = (chatData as any).allFiles || (chatData as any).modifiedFiles;
            setFiles(newFiles);
            addCodeyMessage('✅ Files updated! Check out the preview to see the changes.', 'progress');
          }
          setPrompt(''); // Clear input
          setStatus('complete');
          return;
        }
        if ('type' in chatData && (chatData as any).type === 'chat') {
          // Just a chat message, not a generation request
          if ('message' in chatData) {
            addCodeyMessage((chatData as any).message);
          }
          setPrompt(''); // Clear input
          setStatus('idle');
          return;
        }
        // It's a generation request!
        if ('message' in chatData) {
          addCodeyMessage((chatData as any).message);
        } else {
          // Default conversational acknowledgment
          addCodeyMessage("Awesome! Let me work my magic on that...   ");
        }
        // Clear the input field
        setPrompt('');
        // Start generation with enhanced prompt and userId for token tracking
        const response = await fetch(`${WORKER_URL}/generate/start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            prompt: 'enhancedPrompt' in chatData ? (chatData as any).enhancedPrompt : prompt.trim(),
            userId: user?.email // Pass user email for token deduction
          })
        });
        const data = await response.json();
        if (!response.ok || !(data && typeof data === 'object' && 'jobId' in data)) {
          throw new Error(data && typeof data === 'object' && 'error' in data ? (data as any).error : 'Failed to start generation');
        }
        console.log('   Generation started:', (data as any).jobId);
        setJobId((data as any).jobId);
        // Connect to SSE stream
        connectToStream((data as any).jobId);
      }

    } catch (err) {
      console.error('❌ Generation error:', err);
      setError(err instanceof Error ? err.message : 'Failed to start generation');
      setStatus('error');
      addCodeyMessage("😅 Oops! Something went wrong on my end. Mind giving that another shot?");
      setPrompt(''); // Clear input even on error
    }
  };

  // ============================================
  // CONNECT TO SSE STREAM
  // ============================================
  const connectToStream = (currentJobId: string) => {
    eventSourceRef.current?.close();

    setConnectionStatus('connecting');
    const eventSource = new EventSource(`${WORKER_URL}/generate/stream/${currentJobId}`);
    eventSourceRef.current = eventSource;

    // Track connection opened
    eventSource.onopen = () => {
      console.log('[SSE] Connection established');
      setConnectionStatus('connected');
    };

    // Handle progress updates
    eventSource.addEventListener('progress', (e) => {
      const data = JSON.parse(e.data);
      setProgress(data.progress || 0);
      if (data.message) {
        setIsTyping(true);
        setTimeout(() => {
          addCodeyMessage(data.message, 'progress');
          setIsTyping(false);
        }, 300);
      }
    });

    // Handle file updates
    eventSource.addEventListener('file', (e) => {
      const data = JSON.parse(e.data);
      if (data.files) {
        setFiles(prev => ({ ...prev, ...data.files }));
      }

      // Send bundled chat message
      if (data.fileCount && data.totalFiles) {
        addCodeyMessage(`✨ Created ${data.fileCount} of ${data.totalFiles} files so far...`, 'progress');
      }
    });

    // Handle plan
    eventSource.addEventListener('plan', (e) => {
      const planData = JSON.parse(e.data);
      setPlan(planData);
      setModifiedPlan(planData);
      setShowPlanModal(true);
      addCodeyMessage("📋 Alright! I've sketched out a plan for your project. Take a look and let me know if it looks good!", 'plan', planData);
    });

    // Handle completion
    eventSource.addEventListener('complete', (e) => {
      const data = JSON.parse(e.data);
      setStatus(data.status);
      if (data.files) setFiles(data.files);
      if (data.project) setProject(data.project);
      setCanContinue(data.canContinue || false);
      setProgress(100);
      setCurrentStep('Complete!');

      const message = data.status === 'incomplete'
        ? "💪 This project is getting complex! I've created the foundation, but there's more I can add. Click 'Continue Generation' below and I'll add more features, polish the design, and make it even better!"
        : `🎉 Done! Your ${data.project?.name || 'project'} is ready! I've created ${data.totalFiles} files for you. Check out the live preview!\n\n${data.project?.type === 'mobile' ? '📱 For mobile apps, scan the QR code with Expo Go to test on your phone!' : '🌐 The preview is fully interactive - try it out!'}`;

      setTimeout(() => addCodeyMessage(message, 'complete', data), 500);

      setConnectionStatus('disconnected');
      eventSource.close();
    });

    // Handle errors
    eventSource.addEventListener('error', (e: any) => {
      try {
        const data = e.data ? JSON.parse(e.data) : {};
        const errorMsg = data.message || 'Stream connection error';

        setError(errorMsg);
        setStatus('error');
        setCurrentStep('Error');
        addCodeyMessage(`❌ ${errorMsg}`);

        eventSource.close();
      } catch (err) {
        console.error('Error parsing error event:', err);
      }
    });

    // Enhanced error handler with detailed logging and reconnection logic
    eventSource.onerror = (err) => {
      console.error('[SSE] Stream error:', err);

      // Check for QUIC protocol error
      const errorString = String(err);
      if (errorString.includes('QUIC') || errorString.includes('ERR_QUIC_PROTOCOL_ERROR')) {
        setShowQuicError(true);
        console.warn('⚠️ QUIC protocol error detected - switching to polling fallback');

        // Close SSE and switch to polling
        eventSource.close();
        startPollingFallback(currentJobId);
        return;
      }

      if (eventSource.readyState === EventSource.CONNECTING) {
        console.log('[SSE] Attempting to reconnect...');
        setConnectionStatus('connecting');
        setCurrentStep('Reconnecting...');
      } else if (eventSource.readyState === EventSource.CLOSED) {
        console.log('[SSE] Stream closed - starting polling fallback');
        setConnectionStatus('disconnected');

        // Only show error if we haven't already completed
        if (status !== 'complete' && status !== 'incomplete') {
          // Check if this was a natural completion vs. an error
          const isNaturalClose = progress === 100 || status === 'complete';

          if (!isNaturalClose) {
            setCurrentStep('Switching to fallback mode...');
            addCodeyMessage('🔄 Connection interrupted - switching to polling mode...');

            // Start polling fallback
            startPollingFallback(currentJobId);
          }
        }
      } else if (eventSource.readyState === EventSource.OPEN) {
        console.log('[SSE] Stream is open but encountered an error');
        setConnectionStatus('connected');
      }
    };
  };

  // ============================================
  // POLLING FALLBACK (when SSE fails due to QUIC)
  // ============================================
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startPollingFallback = (pollingJobId: string) => {
    console.log('[Polling] Starting fallback polling for job:', pollingJobId);

    // Clear any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    const pollJob = async () => {
      try {
        const response = await fetch(`${WORKER_URL}/generate/stream/${pollingJobId}`);

        if (!response.ok) {
          console.error('[Polling] Failed:', response.status);
          return;
        }

        // Read the stream once to get current state
        const reader = response.body?.getReader();
        if (!reader) return;

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));

                // Update state based on event type
                if (line.includes('event: progress')) {
                  setProgress(data.progress || 0);
                  setStatus(data.status || 'generating');
                  setCurrentStep(data.message || '');
                  if (data.message) addCodeyMessage(data.message, 'progress');
                } else if (line.includes('event: file')) {
                  if (data.files) setFiles(prev => ({ ...prev, ...data.files }));
                  if (data.latestFile) addCodeyMessage(`📄 Generated ${data.latestFile}`, 'file');
                } else if (line.includes('event: complete')) {
                  setStatus(data.status);
                  if (data.files) setFiles(data.files);
                  if (data.project) setProject(data.project);
                  setCanContinue(data.canContinue || false);
                  setProgress(100);
                  addCodeyMessage('✅ Generation complete!', 'complete');

                  // Stop polling
                  if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                  }
                  return;
                }
              } catch (e) {
                console.error('[Polling] Parse error:', e);
              }
            }
          }
        }
      } catch (err) {
        console.error('[Polling] Error:', err);
      }
    };

    // Poll every 2 seconds
    pollJob(); // Initial poll
    pollingIntervalRef.current = setInterval(pollJob, 2000);
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // ============================================
  // CONTINUE GENERATION
  // ============================================
  const handleContinue = async () => {
    if (!jobId) return;

    setStatus('generating');
    setCanContinue(false);
    setCurrentStep('Continuing...');
    addCodeyMessage('🔄 Continuing generation with more features...', 'progress');

    try {
      const response = await fetch(`${WORKER_URL}/generate/continue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jobId,
          userId: user?.email // Pass user email for token deduction
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data && typeof data === 'object' && 'error' in data ? (data as any).error : 'Failed to continue generation');
      }

      connectToStream(jobId);

    } catch (err) {
      console.error('❌ Continue error:', err);
      setError(err instanceof Error ? err.message : 'Failed to continue');
      setStatus('error');
      addCodeyMessage('❌ Failed to continue. Please try again.');
    }
  };

  // ============================================
  // SAVE PROJECT
  // ============================================
  const [isSaving, setIsSaving] = useState(false);
  const [savedProjectId, setSavedProjectId] = useState<number | null>(null);

  const handleSave = async () => {
    if (!project || Object.keys(files).length === 0) {
      setError('Nothing to save yet. Generate a project first.');
      return;
    }

    if (!user) {
      setError('Please login to save projects');
      return;
    }

    // Check if already saved
    if (savedProjectId) {
      addCodeyMessage('✅ This project is already saved to your dashboard!', 'progress');
      return;
    }

    if (isSaving) {
      addCodeyMessage('⏳ Already saving... please wait!', 'progress');
      return;
    }

    try {
      setIsSaving(true);
      addCodeyMessage('💾 Saving your project...', 'progress');

      // 1. Create project via API
      const response = await workerAPI.createProject({
        name: project.name,
        description: project.description,
        project_type: project.type,
        framework: project.framework,
      });

      if (response.success && response.data?.project) {
        const projectId = response.data.project.id;
        setSavedProjectId(parseInt(projectId));

        // 2. Upload files to R2
        addCodeyMessage('📤 Uploading files to cloud storage...', 'progress');

        const uploadResponse = await workerAPI.saveProjectFiles(projectId, files);

        if (!uploadResponse.success) {
          throw new Error('Failed to upload files to storage');
        }

        addCodeyMessage(`✅ Saved "${project.name}" with ${Object.keys(files).length} files to your dashboard!`, 'progress');
      } else {
        throw new Error('Failed to create project');
      }

    } catch (err: any) {
      console.error('Save error:', err);
      // Check if it's a duplicate error
      if (err.message && err.message.toLowerCase().includes('already exists')) {
        addCodeyMessage('ℹ️ This project is already saved to your dashboard!', 'progress');
        // Don't show it as an error
      } else {
        setError(err instanceof Error ? err.message : 'Failed to save project');
        addCodeyMessage('❌ Failed to save project. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  // ============================================
  // PLAN APPROVAL
  // ============================================
  const approvePlan = async () => {
    setShowPlanModal(false);
    try {
      await fetch(`${WORKER_URL}/generate/approve-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jobId, approved: true })
      });
    } catch {}
  };

  // ============================================
  // HELPER: ADD MESSAGES (with deduplication)
  // ============================================
  const addCodeyMessage = (content: string, type?: string, data?: any) => {
    const messageId = `codey-${type || 'msg'}-${Date.now()}`;

    setChatMessages(prev => {
      // Check if similar message already exists (prevent duplication)
      const hasSimilar = prev.some(msg =>
        msg.role === 'codey' &&
        msg.content === content &&
        Date.now() - msg.timestamp < 2000 // within 2 seconds
      );

      if (hasSimilar) {
        console.log('⚠️ Prevented duplicate message:', content.substring(0, 50));
        return prev;
      }

      return [...prev, {
        id: messageId,
        role: 'codey',
        content,
        type: type as any,
        data,
        timestamp: Date.now()
      }];
    });
  };

  const addUserMessage = (content: string) => {
    const messageId = `user-${Date.now()}`;

    setChatMessages(prev => [...prev, {
      id: messageId,
      role: 'user',
      content,
      timestamp: Date.now()
    }]);
  };

  // ============================================
  // RENDER
  // ============================================
  const isLoading = status === 'generating' || status === 'awaiting-approval';
  const hasFiles = Object.keys(files).length > 0;

  // File filtering
  const filteredFiles = Object.keys(files).filter(path =>
    path.toLowerCase().includes(fileSearchQuery.toLowerCase())
  );

  return (
    <div className={`h-screen overflow-hidden flex flex-col font-sans ${
      isDarkMode
        ? 'bg-gradient-to-br from-[#0F0F11] via-[#121212] to-[#0F0F11]'
        : 'bg-gradient-to-br from-blue-50 via-white to-purple-50'
    }`}>
      {/* Top Bar - Premium Minimal */}
      <div className={`h-12 backdrop-blur-xl border-b flex items-center px-4 flex-shrink-0 ${
        isDarkMode
          ? 'bg-black/20 border-white/5'
          : 'bg-white/40 border-gray-200/20'
      }`}>
        <div className="flex items-center gap-3">
          <CodemineLogo
            size={28}
            animated={true}
            pulsing={isLoading}
          />
          <div>
            <h1 className={`text-lg font-semibold tracking-tight ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Codey's Workspace
            </h1>
            {isLoading && (
              <div className={`text-xs font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                {currentStep || 'Generating...'}
              </div>
            )}
          </div>
        </div>

        <div className="ml-auto flex items-center gap-2">
          {/* Token Usage Display */}
          {user && (
            <TokenUsageDisplay isDarkMode={isDarkMode} compact={true} />
          )}

          {isLoading && (
            <div className={`flex items-center gap-2 px-2.5 py-1 rounded-full ${
              isDarkMode
                ? 'bg-[#6C63FF]/15 border border-[#A259FF]/20'
                : 'bg-blue-50 border border-blue-200'
            }`}>
              <div className="flex gap-1">
                <div className={`w-1 h-1 rounded-full animate-bounce ${isDarkMode ? 'bg-[#A259FF]' : 'bg-blue-500'}`} style={{ animationDelay: '0ms' }}></div>
                <div className={`w-1 h-1 rounded-full animate-bounce ${isDarkMode ? 'bg-[#A259FF]' : 'bg-blue-500'}`} style={{ animationDelay: '150ms' }}></div>
                <div className={`w-1 h-1 rounded-full animate-bounce ${isDarkMode ? 'bg-[#A259FF]' : 'bg-blue-500'}`} style={{ animationDelay: '300ms' }}></div>
              </div>
              <span className={`text-xs font-medium tracking-wide ${
                isDarkMode ? 'text-[#A259FF]' : 'text-blue-700'
              }`}>{Math.round(progress)}%</span>
            </div>
          )}
          {hasFiles && user && (
            <button
              onClick={handleSave}
              disabled={isSaving || !!savedProjectId}
              className={`px-3 py-1 rounded-md text-white text-xs font-medium transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                savedProjectId
                  ? isDarkMode
                    ? 'bg-gradient-to-r from-gray-600 to-gray-700'
                    : 'bg-gradient-to-r from-gray-400 to-gray-500'
                  : isDarkMode
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 hover:shadow-emerald-500/20'
                    : 'bg-gradient-to-r from-emerald-400 to-teal-400 hover:from-emerald-500 hover:to-teal-500 hover:shadow-emerald-400/15 shadow-sm'
              }`}
            >
              {savedProjectId ? '✓ Saved' : isSaving ? '⏳ Saving...' : '💾 Save'}
            </button>
          )}
        </div>
      </div>

      {/* QUIC Error Banner */}
      {showQuicError && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 px-6 py-3">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-200">Network Protocol Issue Detected</h3>
              <p className="text-xs text-amber-800 dark:text-amber-300 mt-1">
                QUIC protocol errors may prevent preview from loading. <strong>Quick fix:</strong> Open{' '}
                <code className="px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 font-mono text-[10px]">chrome://flags/#enable-quic</code>
                {' '}→ Set to <strong>Disabled</strong> → Restart browser.{' '}
                <a
                  href="https://github.com/Codeminelive/Copy-codemine-Space/blob/main/docs/TROUBLESHOOTING_QUIC_ERROR.md"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-semibold hover:text-amber-900 dark:hover:text-amber-100"
                >
                  Full guide here
                </a>
              </p>
            </div>
            <button
              onClick={() => setShowQuicError(false)}
              className="text-amber-600 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-200 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* DESKTOP LAYOUT */}
        <div className="hidden md:flex flex-1">
          {/* Left: Chat Panel (30%) - Premium Minimal */}
          <div className={`w-[30%] min-w-[280px] max-w-[400px] border-r flex flex-col ${
            isDarkMode
              ? 'bg-slate-900/90 border-white/5'
              : 'bg-white/80 backdrop-blur-xl border-gray-200/20'
          }`}>
            {/* Chat Header - Minimal */}
            <div className={`h-10 px-3 border-b flex items-center justify-between ${
              isDarkMode
                ? 'border-white/5 bg-slate-800/40'
                : 'border-gray-200/20 bg-gray-50/40'
            }`}>
              <h2 className={`text-sm font-medium ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>Codey</h2>
            </div>

            {/* Chat Messages - Adaptive Theme */}
            <div className={`flex-1 overflow-y-auto px-3 py-4 space-y-3 ${
              isDarkMode
                ? 'bg-gradient-to-b from-slate-900 to-black'
                : 'bg-gradient-to-b from-white/20 to-white/40'
            }`}>
              {chatMessages.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full text-center px-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 shadow-lg animate-pulse ${
                    isDarkMode
                      ? 'bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 shadow-blue-500/30'
                      : 'bg-gradient-to-br from-blue-400 via-blue-500 to-indigo-500 shadow-blue-400/20'
                  }`}>
                    <span className="text-2xl">👋</span>
                  </div>
                  <h3 className={`text-lg font-semibold mb-2 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>Hey! I'm Codey</h3>
                  <p className={`text-sm font-medium mb-4 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    Let's build something amazing together!
                  </p>
                  <div className={`grid grid-cols-1 gap-2 w-full max-w-xs text-xs ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">🌐</span>
                      <span className="font-medium">Websites with live preview</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">📱</span>
                      <span className="font-medium">Mobile apps with Expo Snack</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">🎮</span>
                      <span className="font-medium">Games with instant playback</span>
                    </div>
                  </div>
                </div>
              )}

              {displayedMessages.map((msg) => (
                <div key={msg.id} className={`flex gap-2 animate-fade-in ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className={`w-6 h-6 rounded-lg flex items-center justify-center text-sm flex-shrink-0 shadow-sm ${
                    msg.role === 'codey'
                      ? isDarkMode
                        ? 'bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 shadow-blue-500/30'
                        : 'bg-gradient-to-br from-blue-400 via-blue-500 to-indigo-500 shadow-blue-400/20'
                      : 'bg-gradient-to-r from-blue-500 to-indigo-500 shadow-blue-500/30'
                  }`}>
                    {msg.role === 'codey' ? (
                      <CodemineLogoSimple size={16} />
                    ) : (
                      '👤'
                    )}
                  </div>
                  <div className={`flex-1 ${msg.role === 'user' ? 'text-right' : ''}`}>
                    <div className={`inline-block max-w-[85%] px-3 py-2 rounded-lg text-xs leading-relaxed shadow-sm transition-all hover:scale-[1.01] ${
                      msg.role === 'user'
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-medium border border-white/20'
                        : isDarkMode
                          ? 'bg-slate-800/70 backdrop-blur-sm text-white font-normal border border-white/5'
                          : 'bg-white/60 backdrop-blur-xl text-gray-800 font-normal border border-gray-200/30 shadow-sm'
                    }`}>
                      <div className="whitespace-pre-wrap break-words">{msg.content}</div>
                    </div>
                    <div className={`text-xs mt-1 font-medium ${
                      msg.role === 'user' ? 'text-right' : ''
                    } ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ))}

              {/* Show Codey Thinking when generating starts */}
              {status === 'generating' && !hasFiles && displayedMessages.length > 0 && (
                <CodeyThinking
                  isThinking={true}
                  onThinkingComplete={() => setThinkingTime(0)}
                />
              )}

              {/* Show planning message when plan is ready */}
              {plan && status === 'awaiting-approval' && (
                <PlanningMessage plan={plan} />
              )}

              {/* Show progress messages during generation */}
              {currentStep && status === 'generating' && hasFiles && (
                <ProgressMessage
                  message={currentStep}
                  emoji="⚡"
                />
              )}

              {/* Typing Indicator - Adaptive */}
              {isTyping && (
                <div className="flex gap-2">
                  <div className={`w-6 h-6 rounded-lg flex items-center justify-center shadow-sm ${
                    isDarkMode
                      ? 'bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 shadow-blue-500/30'
                      : 'bg-gradient-to-br from-blue-400 via-blue-500 to-indigo-500 shadow-blue-400/20'
                  }`}>
                    <CodemineLogoSimple size={16} />
                  </div>
                  <div className={`backdrop-blur-sm px-3 py-2 rounded-lg shadow-sm ${
                    isDarkMode
                      ? 'bg-slate-800/70 border border-white/5'
                      : 'bg-white/60 border border-gray-200/30'
                  }`}>
                    <div className="flex gap-1">
                      <div className={`w-1.5 h-1.5 rounded-full animate-bounce shadow-sm ${
                        isDarkMode ? 'bg-blue-400 shadow-blue-400/30' : 'bg-blue-500 shadow-blue-500/20'
                      }`} style={{ animationDelay: '0ms' }}></div>
                      <div className={`w-1.5 h-1.5 rounded-full animate-bounce shadow-sm ${
                        isDarkMode ? 'bg-indigo-400 shadow-indigo-400/30' : 'bg-indigo-500 shadow-indigo-500/20'
                      }`} style={{ animationDelay: '150ms' }}></div>
                      <div className={`w-1.5 h-1.5 rounded-full animate-bounce shadow-sm ${
                        isDarkMode ? 'bg-blue-400 shadow-blue-400/30' : 'bg-blue-500 shadow-blue-500/20'
                      }`} style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={chatEndRef} />
            </div>

            {/* Input Area - Adaptive */}
            <div className={`p-3 border-t backdrop-blur-xl ${
              isDarkMode
                ? 'border-white/5 bg-black/30'
                : 'border-gray-200/20 bg-white/20'
            }`}>
              {error && (
                <div className={`mb-2 px-3 py-2 rounded-lg text-xs font-medium backdrop-blur-sm ${
                  isDarkMode
                    ? 'bg-red-500/15 border border-red-400/20 text-red-200'
                    : 'bg-red-50 border border-red-200 text-red-700'
                }`}>
                  {error}
                </div>
              )}

              {canContinue && status === 'incomplete' && (
                <div className="mb-2">
                  <button
                    onClick={handleContinue}
                    className={`w-full px-3 py-2 rounded-lg text-white text-xs font-semibold hover:shadow-lg transition-all ${
                      isDarkMode
                        ? 'bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 hover:shadow-pink-500/30 border border-white/20'
                        : 'bg-gradient-to-r from-orange-400 via-pink-400 to-purple-400 hover:shadow-pink-400/20 border border-white/30 shadow-sm'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-1">
                      <span className="text-sm">⚡</span>
                      <span>Continue Generation</span>
                      <span className="text-sm">⚡</span>
                    </div>
                    <div className={`text-xs mt-0.5 font-medium opacity-90 ${
                      isDarkMode ? 'text-white/90' : 'text-white'
                    }`}>
                      Project needs more work - click to continue
                    </div>
                  </button>
                </div>
              )}

              <div className="flex gap-2">
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !e.shiftKey) {
                      e.preventDefault();
                      handleGenerate();
                    }
                  }}
                  placeholder="Type your message..."
                  className={`flex-1 px-3 py-2 text-xs border rounded-lg focus:ring-1 transition-all backdrop-blur-sm font-normal resize-none ${
                    isDarkMode
                      ? 'border-white/10 focus:ring-blue-500 focus:border-blue-500/30 bg-slate-800/40 text-white placeholder:text-gray-500'
                      : 'border-gray-200/30 focus:ring-blue-400 focus:border-blue-400/30 bg-white/50 text-gray-900 placeholder:text-gray-500 shadow-sm'
                  }`}
                  rows={2}
                  disabled={isLoading}
                />
                <button
                  onClick={handleGenerate}
                  disabled={isLoading || !prompt.trim()}
                  className={`px-3 text-white font-medium rounded-lg hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center border ${
                    isDarkMode
                      ? 'bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-500 hover:shadow-blue-500/30 border-white/10'
                      : 'bg-gradient-to-r from-blue-400 via-indigo-400 to-blue-400 hover:shadow-blue-400/20 border-white/20 shadow-sm'
                  }`}
                >
                  {isLoading ? (
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    '🚀'
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Right: Preview/Files Panel (70%) */}
          <div className={`flex-1 flex flex-col ${
            isDarkMode
              ? 'bg-gradient-to-br from-slate-900 via-gray-900 to-black'
              : 'bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30'
          }`}>
            {/* Toggle Bar - Adaptive */}
            <div className={`h-10 px-3 border-b flex items-center justify-between backdrop-blur-xl ${
              isDarkMode
                ? 'border-white/5 bg-black/30'
                : 'border-gray-200/20 bg-white/30'
            }`}>
              <div className={`flex items-center gap-1 backdrop-blur-sm rounded-lg p-0.5 border ${
                isDarkMode
                  ? 'bg-slate-800/40 border-white/5'
                  : 'bg-white/50 border-gray-200/20'
              }`}>
                <button
                  onClick={() => setDesktopView('preview')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    desktopView === 'preview'
                      ? isDarkMode
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg shadow-blue-500/30'
                        : 'bg-gradient-to-r from-blue-400 to-indigo-400 text-white shadow-md shadow-blue-400/20'
                      : isDarkMode
                        ? 'text-gray-400 hover:text-white'
                        : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  🖥️ Preview
                </button>
                <button
                  onClick={() => setDesktopView('files')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    desktopView === 'files'
                      ? isDarkMode
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg shadow-blue-500/30'
                        : 'bg-gradient-to-r from-blue-400 to-indigo-400 text-white shadow-md shadow-blue-400/20'
                      : isDarkMode
                        ? 'text-gray-400 hover:text-white'
                        : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  📁 Files {hasFiles && `(${Object.keys(files).length})`}
                </button>
              </div>

              {(desktopView === 'preview' && hasFiles) || isLoading ? (
                <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full backdrop-blur-sm ${
                  isDarkMode
                    ? 'bg-emerald-500/15 border border-emerald-400/20'
                    : 'bg-emerald-50 border border-emerald-200'
                }`}>
                  <div className={`w-1.5 h-1.5 rounded-full animate-pulse shadow-sm ${
                    isDarkMode ? 'bg-emerald-400 shadow-emerald-400/30' : 'bg-emerald-500 shadow-emerald-500/20'
                  }`}></div>
                  <span className={`text-xs font-medium tracking-wide ${
                    isDarkMode ? 'text-emerald-300' : 'text-emerald-700'
                  }`}>Live</span>
                </div>
              ) : null}
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-hidden">
              {desktopView === 'preview' ? (
                // Preview View
                <div className="h-full w-full">
                  {isLoading && !hasFiles && (
                    <AnimatedCodePreview
                      isDarkMode={isDarkMode}
                      currentStep={currentStep}
                      progress={progress}
                      projectType={project?.type}
                    />
                  )}

                  {hasFiles && project ? (
                    <div className={`h-full flex items-center justify-center p-6 ${
                      isDarkMode
                        ? 'bg-gradient-to-br from-slate-900 to-black'
                        : 'bg-gradient-to-br from-white to-gray-50'
                    }`}>
                      <div className="text-center">
                        <div className={`w-20 h-20 mx-auto mb-4 rounded-2xl flex items-center justify-center shadow-xl ${
                          isDarkMode
                            ? 'bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 shadow-blue-500/50'
                            : 'bg-gradient-to-br from-blue-400 via-blue-500 to-indigo-500 shadow-blue-400/30'
                        }`}>
                          <span className="text-4xl">🖥️</span>
                        </div>
                        <h3 className={`text-xl font-semibold mb-3 ${
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                          Preview Unavailable
                        </h3>
                        <p className={`text-sm font-medium ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>
                          Live preview has been temporarily disabled. Check the files tab to view your code.
                        </p>
                      </div>
                    </div>
                  ) : !isLoading && (
                    <AnimatedCodePreview
                      isDarkMode={isDarkMode}
                      currentStep="Waiting for your next idea..."
                      progress={0}
                      projectType={project?.type}
                    />
                  )}
                </div>
              ) : (
                // Files View
                <div className="h-full flex flex-col bg-white/50 dark:bg-gray-900/50">
                  {hasFiles ? (
                    <>
                      {/* File Search */}
                      <div className="px-4 py-3 border-b border-gray-200/50 dark:border-gray-800/50">
                        <input
                          type="text"
                          value={fileSearchQuery}
                          onChange={(e) => setFileSearchQuery(e.target.value)}
                          placeholder="Search files..."
                          className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all dark:text-white"
                        />
                      </div>

                      {/* Files List & Code Viewer */}
                      <div className="flex-1 flex overflow-hidden">
                        {/* File Tree */}
                        <div className="w-64 border-r border-gray-200/50 dark:border-gray-800/50 overflow-y-auto p-2">
                          {filteredFiles.map((path) => {
                            const isSelected = selectedFile === path;
                            const fileName = path.split('/').pop() || path;
                            const folder = path.includes('/') ? path.substring(0, path.lastIndexOf('/')) : '';
                            const ext = fileName.split('.').pop() || '';

                            let icon = '📄';
                            if (['tsx', 'jsx', 'ts', 'js'].includes(ext)) icon = '⚛️';
                            else if (['css', 'scss'].includes(ext)) icon = '🎨';
                            else if (ext === 'html') icon = '🌐';
                            else if (ext === 'json') icon = '📦';

                            return (
                              <button
                                key={path}
                                onClick={() => setSelectedFile(path)}
                                className={`w-full text-left px-3 py-2 rounded-lg transition-all mb-1 ${
                                  isSelected
                                    ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 shadow-sm'
                                    : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-base">{icon}</span>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium truncate">{fileName}</div>
                                    {folder && (
                                      <div className="text-[10px] text-gray-500 dark:text-gray-500 truncate">{folder}</div>
                                    )}
                                  </div>
                                  {isSelected && (
                                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>

                        {/* Code Viewer */}
                        {selectedFile && (
                          <div className="flex-1 flex flex-col">
                            <div className="h-12 px-4 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200/50 dark:border-gray-800/50 flex items-center justify-between">
                              <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                                {selectedFile}
                              </span>
                            </div>
                            <div className="flex-1 overflow-auto p-4 bg-gray-50 dark:bg-gray-900">
                              <pre className="text-xs font-mono text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">
                                {files[selectedFile]}
                              </pre>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400 dark:text-gray-600">
                      <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                      </svg>
                      <p className="text-sm font-medium">No files yet</p>
                      <p className="text-xs mt-1">Generate a project to see files here</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* MOBILE LAYOUT */}
        <div className="flex md:hidden flex-1 flex-col">
          {/* Mobile Toggle */}
          <div className={`h-14 px-4 border-b flex items-center justify-center backdrop-blur-sm ${
            isDarkMode
              ? 'border-white/10 bg-black/30'
              : 'border-gray-200/30 bg-white/50'
          }`}>
            <div className={`flex items-center gap-1 rounded-lg p-1 ${
              isDarkMode
                ? 'bg-slate-800/50'
                : 'bg-gray-100'
            }`}>
              <button
                onClick={() => setMobileView('chat')}
                className={`px-6 py-2 rounded-md text-sm font-bold transition-all ${
                  mobileView === 'chat'
                    ? isDarkMode
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg shadow-blue-500/30'
                      : 'bg-gradient-to-r from-blue-400 to-indigo-400 text-white shadow-md shadow-blue-400/20'
                    : isDarkMode
                      ? 'text-gray-400 hover:text-white'
                      : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                💬 Chat
              </button>
              <button
                onClick={() => setMobileView('preview')}
                className={`px-6 py-2 rounded-md text-sm font-bold transition-all ${
                  mobileView === 'preview'
                    ? isDarkMode
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg shadow-blue-500/30'
                      : 'bg-gradient-to-r from-blue-400 to-indigo-400 text-white shadow-md shadow-blue-400/20'
                    : isDarkMode
                      ? 'text-gray-400 hover:text-white'
                      : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                🖥️ Preview
              </button>
            </div>
          </div>

          {/* Mobile Content */}
          <div className="flex-1 overflow-hidden">
            {mobileView === 'chat' ? (
              // Mobile Chat View
              <div className={`h-full flex flex-col ${
                isDarkMode
                  ? 'bg-gradient-to-b from-slate-900 to-black'
                  : 'bg-gradient-to-b from-white to-gray-50'
              }`}>
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
                  {chatMessages.length === 0 && (
                    <div className="flex flex-col items-center justify-center h-full text-center px-6">
                      <div className="mb-4">
                        <CodemineLogo size={64} animated={true} pulsing={false} />
                      </div>
                      <h3 className={`text-lg font-semibold mb-2 ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>Hi! I'm Codey</h3>
                      <p className={`text-sm font-medium ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        Describe your project and I'll build it for you!
                      </p>
                    </div>
                  )}

                  {displayedMessages.map((msg, idx) => (
                    <div key={idx} className={`flex gap-2.5 animate-fade-in ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-sm flex-shrink-0 shadow-sm ${
                        msg.role === 'codey'
                          ? isDarkMode
                            ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white'
                            : 'bg-gradient-to-br from-blue-400 to-indigo-500 text-white'
                          : 'bg-gradient-to-br from-blue-600 to-cyan-600 text-white'
                      }`}>
                        {msg.role === 'codey' ? (
                          <CodemineLogoSimple size={18} />
                        ) : (
                          '👤'
                        )}
                      </div>
                      <div className={`flex-1 ${msg.role === 'user' ? 'text-right' : ''}`}>
                        <div className={`inline-block max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed shadow-sm ${
                          msg.role === 'user'
                            ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-medium'
                            : isDarkMode
                              ? 'bg-slate-800/80 text-white font-normal border border-white/10'
                              : 'bg-white text-gray-900 font-normal border border-gray-200 shadow-md'
                        }`}>
                          <div className="whitespace-pre-wrap break-words">{msg.content}</div>
                        </div>
                        <div className={`text-xs mt-1 font-medium ${
                          msg.role === 'user' ? 'text-right' : ''
                        } ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                  ))}

                  {isTyping && (
                    <div className="flex gap-2.5">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-white text-sm shadow-sm ${
                        isDarkMode
                          ? 'bg-gradient-to-br from-blue-500 to-indigo-600'
                          : 'bg-gradient-to-br from-blue-400 to-indigo-500'
                      }`}>
                        <CodemineLogoSimple size={18} />
                      </div>
                      <div className={`px-4 py-3 rounded-2xl shadow-sm ${
                        isDarkMode
                          ? 'bg-slate-800/80 border border-white/10'
                          : 'bg-white border border-gray-200'
                      }`}>
                        <div className="flex gap-1.5">
                          <div className={`w-2 h-2 rounded-full animate-bounce ${
                            isDarkMode ? 'bg-blue-400' : 'bg-blue-500'
                          }`} style={{ animationDelay: '0ms' }}></div>
                          <div className={`w-2 h-2 rounded-full animate-bounce ${
                            isDarkMode ? 'bg-indigo-400' : 'bg-indigo-500'
                          }`} style={{ animationDelay: '150ms' }}></div>
                          <div className={`w-2 h-2 rounded-full animate-bounce ${
                            isDarkMode ? 'bg-blue-400' : 'bg-blue-500'
                          }`} style={{ animationDelay: '300ms' }}></div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={chatEndRef} />
                </div>

                <div className={`p-4 border-t backdrop-blur-xl ${
                  isDarkMode
                    ? 'border-white/10 bg-black/40'
                    : 'border-gray-200/30 bg-white/90'
                }`}>
                  {error && (
                    <div className={`mb-3 px-3 py-2 rounded-lg text-xs font-medium ${
                      isDarkMode
                        ? 'bg-red-500/20 border border-red-400/30 text-red-200'
                        : 'bg-red-50 border border-red-200 text-red-700'
                    }`}>
                      {error}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <textarea
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder="Ask me anything or describe what you want to build..."
                      className={`flex-1 px-4 py-3 text-sm border-2 rounded-lg focus:ring-2 transition-all resize-none font-normal ${
                        isDarkMode
                          ? 'border-white/20 focus:ring-blue-500 focus:border-blue-500/50 bg-slate-800/50 text-white placeholder:text-gray-400'
                          : 'border-gray-200 focus:ring-blue-400 focus:border-blue-400/50 bg-white text-gray-900 placeholder:text-gray-500 shadow-sm'
                      }`}
                      rows={2}
                      disabled={isLoading}
                    />
                    <button
                      onClick={handleGenerate}
                      disabled={isLoading || !prompt.trim()}
                      className={`px-5 text-white font-semibold rounded-lg transition-all disabled:opacity-50 ${
                        isDarkMode
                          ? 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:shadow-lg hover:shadow-blue-500/30'
                          : 'bg-gradient-to-r from-blue-400 to-indigo-400 hover:shadow-lg hover:shadow-blue-400/20 shadow-sm'
                      }`}
                    >
                      {isLoading ? '⏳' : '🚀'}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              // Mobile Preview View
              <div className="h-full">
                {hasFiles && project ? (
                  <div className={`h-full flex items-center justify-center p-6 ${
                    isDarkMode
                      ? 'bg-gradient-to-br from-slate-900 to-black'
                      : 'bg-gradient-to-br from-white to-gray-50'
                  }`}>
                    <div className="text-center">
                      <div className={`w-20 h-20 mx-auto mb-4 rounded-2xl flex items-center justify-center shadow-xl ${
                        isDarkMode
                          ? 'bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 shadow-blue-500/50'
                          : 'bg-gradient-to-br from-blue-400 via-blue-500 to-indigo-500 shadow-blue-400/30'
                      }`}>
                        <span className="text-4xl">🖥️</span>
                      </div>
                      <h3 className={`text-xl font-semibold mb-3 ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        Preview Unavailable
                      </h3>
                      <p className={`text-sm font-medium ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        Live preview has been temporarily disabled. Check the files tab to view your code.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className={`h-full flex items-center justify-center p-6 ${
                    isDarkMode
                      ? 'bg-gradient-to-br from-slate-900 to-black'
                      : 'bg-gradient-to-br from-white to-gray-50'
                  }`}>
                    <div className="text-center">
                      <div className={`w-20 h-20 mx-auto mb-4 rounded-2xl flex items-center justify-center shadow-xl ${
                        isDarkMode
                          ? 'bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 shadow-blue-500/50'
                          : 'bg-gradient-to-br from-blue-400 via-blue-500 to-indigo-500 shadow-blue-400/30'
                      }`}>
                        <span className="text-4xl">🖥️</span>
                      </div>
                      <h3 className={`text-xl font-semibold mb-3 ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        No Preview Yet
                      </h3>
                      <p className={`text-sm font-medium ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        Generate a project in the chat to see it here
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Plan Modal */}
      {showPlanModal && plan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-fade-in">
          <div className="w-full max-w-3xl bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-800/50 overflow-hidden animate-scale-in">
            <div className="px-6 py-5 bg-gradient-to-r from-purple-50 via-white to-blue-50 dark:from-purple-900/10 dark:via-gray-900 dark:to-blue-900/10 border-b border-gray-200/50 dark:border-gray-800/50 flex items-center">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
                  <span className="text-2xl">📋</span>
                </div>
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Review Your Plan</h2>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Take a moment to review before I start building</p>
                </div>
              </div>
              <button
                onClick={() => setShowPlanModal(false)}
                className="ml-auto w-10 h-10 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center transition-all"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <div className="space-y-6">
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                    Project Name
                  </h3>
                  <p className="text-xl font-semibold text-gray-900 dark:text-white">{plan.projectName}</p>
                </div>

                <div>
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                    Description
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed font-normal">{plan.enhancedPrompt}</p>
                </div>

                <div>
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    Features ({plan.features?.length || 0})
                  </h3>
                  <ul className="space-y-2">
                    {plan.features?.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <div className="w-5 h-5 mt-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                          <svg className="w-3 h-3 text-emerald-600 dark:text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="text-gray-700 dark:text-gray-300 text-sm font-normal">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                      Tech Stack
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {plan.techStack?.map((tech, idx) => (
                        <span key={idx} className="px-3 py-1.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-medium">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                      Files to Generate
                    </h3>
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                        <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9 2a2 2 0 00-2 2v8a2 2 0 002 2h6a2 2 0 002-2V6.414A2 2 0 0016.414 5L14 2.586A2 2 0 0012.586 2H9z" />
                        </svg>
                      </div>
                      <span className="text-3xl font-semibold text-gray-900 dark:text-white">{plan.files?.length || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200/50 dark:border-gray-800/50 flex gap-3">
              <button
                onClick={approvePlan}
                className="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold hover:shadow-xl hover:shadow-purple-500/30 transition-all"
              >
                ✓ Approve & Start Building
              </button>
              <button
                onClick={() => {
                  setShowPlanModal(false);
                  setShowModifyModal(true);
                }}
                className="px-6 py-3 rounded-xl bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-all"
              >
                Modify
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modify Plan Modal */}
      {showModifyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-fade-in">
          <div className={`w-full max-w-2xl rounded-3xl shadow-2xl border overflow-hidden animate-scale-in ${
            isDarkMode
              ? 'bg-slate-900 border-white/10'
              : 'bg-white border-gray-200'
          }`}>
            <div className={`px-6 py-5 border-b flex items-center ${
              isDarkMode
                ? 'bg-gradient-to-r from-blue-900/30 to-indigo-900/30 border-white/10'
                : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-gray-200'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg ${
                  isDarkMode
                    ? 'bg-gradient-to-br from-blue-500 to-indigo-600 shadow-blue-500/30'
                    : 'bg-gradient-to-br from-blue-400 to-indigo-500 shadow-blue-400/20'
                }`}>
                  <span className="text-2xl">✏️</span>
                </div>
                <div>
                  <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Modify Plan
                  </h2>
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Describe the changes you'd like to make
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowModifyModal(false);
                  setModifyInput('');
                }}
                className={`ml-auto w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                  isDarkMode
                    ? 'hover:bg-slate-800 text-gray-400'
                    : 'hover:bg-gray-100 text-gray-600'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              <textarea
                value={modifyInput}
                onChange={(e) => setModifyInput(e.target.value)}
                placeholder="Example: Add user authentication, change the color scheme to dark mode, include a payment system..."
                className={`w-full h-32 px-4 py-3 rounded-xl border-2 focus:ring-2 transition-all font-normal resize-none ${
                  isDarkMode
                    ? 'bg-slate-800 border-white/20 focus:ring-blue-500 focus:border-blue-500/50 text-white placeholder:text-gray-400'
                    : 'bg-white border-gray-300 focus:ring-blue-400 focus:border-blue-400 text-gray-900 placeholder:text-gray-500'
                }`}
                autoFocus
              />

              <div className="mt-6">
                <p className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  How should I handle your changes?
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      if (!modifyInput.trim()) return;
                      addCodeyMessage(`Got it! Let me enhance the plan with: "${modifyInput.trim()}". I'll make it even better! ✨`);
                      setShowModifyModal(false);
                      setModifyInput('');
                      // TODO: Send enhanced request to backend
                    }}
                    className={`flex-1 px-5 py-3 rounded-xl text-white font-semibold transition-all hover:shadow-xl border ${
                      isDarkMode
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:shadow-blue-500/50 border-white/20'
                        : 'bg-gradient-to-r from-blue-400 to-indigo-400 hover:shadow-blue-400/30 border-white/30'
                    }`}
                  >
                    🌟 Enhance It
                  </button>
                  <button
                    onClick={() => {
                      if (!modifyInput.trim()) return;
                      addCodeyMessage(`Perfect! I'll follow your exact requirements: "${modifyInput.trim()}" 🎯`);
                      setShowModifyModal(false);
                      setModifyInput('');
                      // TODO: Send specific request to backend
                    }}
                    className={`flex-1 px-5 py-3 rounded-xl font-semibold transition-all hover:shadow-xl border ${
                      isDarkMode
                        ? 'bg-slate-800 hover:bg-slate-700 text-white border-white/20'
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-900 border-gray-300'
                    }`}
                  >
                    🎯 Be Specific
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Animations & Fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700;800;900&family=Inter:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@300;400;500;600;700&display=swap');

        * {
          font-family: 'Geist', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-feature-settings: 'cv02', 'cv03', 'cv04', 'cv11';
        }

        code, pre, .font-code {
          font-family: 'JetBrains Mono', 'Roboto Mono', 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
          font-feature-settings: 'liga' 1, 'calt' 1;
        }

        .font-display {
          font-family: 'Geist', 'Inter', sans-serif;
          font-weight: 800;
          letter-spacing: -0.02em;
        }

        .font-body {
          font-family: 'Geist', 'Inter', sans-serif;
          font-weight: 400;
          line-height: 1.6;
        }

        .font-medium {
          font-family: 'Geist', 'Inter', sans-serif;
          font-weight: 500;
        }

        .font-semibold {
          font-family: 'Geist', 'Inter', sans-serif;
          font-weight: 600;
        }

        .font-bold {
          font-family: 'Geist', 'Inter', sans-serif;
          font-weight: 700;
        }

        .font-black {
          font-family: 'Geist', 'Inter', sans-serif;
          font-weight: 900;
          letter-spacing: -0.01em;
        }

        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
        .animate-scale-in {
          animation: scale-in 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }

        @keyframes typing {
          from { width: 0; }
          to { width: 100%; }
        }

        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }

        @keyframes pulse-glow {
          0%, 100% { opacity: 0.5; box-shadow: 0 0 20px currentColor; }
          50% { opacity: 1; box-shadow: 0 0 40px currentColor; }
        }

        .code-line {
          animation: typing 0.5s steps(30) forwards;
          overflow: hidden;
          white-space: nowrap;
        }

        .cursor {
          animation: blink 1s step-end infinite;
        }
      `}</style>
    </div>
  );
}
